// CapaActionStatus Configuration - UNIFIED APPROACH
// Manages CAPA Action statuses (Not Started, In Progress, Completed, Verified, Overdue)
// Based on Prisma CapaActionStatus model schema

import { createTrackingConfig } from '../../../components/generic/labels';
import { tableConfig, uiConfig } from '../../../config/appConfig';

export const capaActionStatusConfig = {
  entityName: 'CapaActionStatus',
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/capa-action-status',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      bulkDelete: '/bulk-delete',
      // CapaActionStatus specific endpoints
      activate: (id) => `/${id}/activate`,
      deactivate: (id) => `/${id}/deactivate`,
      reorder: '/reorder'
    },
    lookupEndpoints: {
      // CAPA action statuses typically don't have lookup dependencies
    },
    // SWR cache invalidation
    swrInvalidation: {
      mainList: '/capa-action-status'
    }
  },
  
  // UI Configuration - Using centralized app config
  ui: {
    drawerWidth: '600px',
    defaultPageSize: tableConfig.defaultPageSize,
    pageSizeOptions: tableConfig.pageSizeOptions,
    defaultTableDensity: tableConfig.defaultDensity,
    requiredFieldStyle: uiConfig.requiredFieldStyle,
    tableVariant: 'default',
    splitView: false,
    searchInputMaxWidth: uiConfig.searchInputMaxWidth,
  },

  // Features configuration
  features: {
    fileAttachments: false,
  },
  
  // Service configuration
  service: {
    name: 'capaActionStatusService',
    methods: {
      getAll: 'getAllCapaActionStatuses',
      getById: 'getCapaActionStatusById',
      create: 'createCapaActionStatus',
      update: 'updateCapaActionStatus',
      delete: 'deleteCapaActionStatus',
      validate: 'validateData'
    }
  },
  
  // Display Configuration
  displayName: 'CAPA Action Status',
  description: 'Manage CAPA action statuses for tracking action progress',
  icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
  
  // UNIFIED FIELD CONFIGURATION
  fields: [
    { 
      name: 'id', 
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    {
      name: 'name',
      label: 'Status Name',
      type: 'text',
      required: true,
      placeholder: 'Enter status name (e.g., Not Started, In Progress, Completed, Verified, Overdue)',
      table: { 
        header: 'Name', 
        width: '200px', 
        sortable: true 
      },
      filterable: true,
      filterType: 'text',
      validation: {
        required: true,
        minLength: 2,
        maxLength: 100,
        pattern: /^[A-Za-z\s&\-'.()]+$/,
        message: 'Status name must contain only letters, spaces, and common punctuation'
      },
      helperText: 'Used for display in action management interfaces'
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      required: false,
      placeholder: 'Enter description for this status',
      rows: 3,
      table: { 
        header: 'Description', 
        width: '300px', 
        sortable: true 
      },
      filterable: true,
      filterType: 'text',
      validation: {
        maxLength: 500,
        message: 'Description must not exceed 500 characters'
      },
      helperText: 'Optional description explaining what this status represents'
    },
    {
      name: 'isActive',
      label: 'Active',
      type: 'switch',
      required: false,
      defaultValue: true,
      switchLabel: 'Status is active and available for use',
      description: 'Inactive statuses cannot be used for new action records',
      labelPosition: 'left',
      table: { 
        header: 'Status', 
        width: '100px',
        sortable: true,
        cellRenderer: 'boolean'
      },
      filterable: true,
      filterType: 'boolean',
      validation: {
        message: 'Please select active status'
      }
    },
    {
      name: 'isCompleted',
      label: 'Completed Status',
      type: 'switch',
      required: false,
      defaultValue: false,
      switchLabel: 'Completed Status', 
      description: 'Mark if this status represents a completed action', 
      labelPosition: 'left',
      table: { 
        header: 'Completed', 
        width: '120px',
        sortable: true,
        cellRenderer: 'boolean'
      },
      filterable: true,
      filterType: 'boolean',
      validation: {
        message: 'Please select completion status'
      },
      helperText: 'Used to identify statuses that represent completed actions'
    },
    {
      name: 'record_order',
      label: 'Display Order',
      type: 'number',
      required: false,
      placeholder: 'Enter display order (optional)',
      step: '1',
      table: { 
        header: 'Order', 
        width: '100px', 
        sortable: true 
      },
      filterable: false,
      validation: {
        min: 1,
        max: 999,
        integer: true,
        message: 'Display order must be a whole number between 1 and 999'
      },
      helperText: 'Controls the order in dropdown lists and displays',
      decimals: 0,
      allowNegative: false
    },
    {
      name: 'color',
      label: 'Display Color',
      type: 'color',
      required: false,
      placeholder: 'Choose a color for visual categorization',
      table: { 
        header: 'Color', 
        width: '100px',
        sortable: false,
        cellRenderer: 'color'
      },
      filterable: false,
      validation: {
        pattern: /^(#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})|[a-z]+)$/,
        message: 'Please enter a valid color (hex code or color name)'
      },
      helperText: 'Used for visual categorization in lists and reports'
    }
  ],

  // Table Configuration - Dynamic getter that generates columns from fields
  get tableColumns() {
    return this.fields
      .filter(field => field.table !== false && !field.hidden)
      .map(field => ({
        key: field.name,
        header: field.table?.header || field.label,
        width: field.table?.width || 'auto',
        sortable: field.table?.sortable !== false,
        displayField: field.table?.displayField || field.name,
        cellRenderer: field.table?.cellRenderer || 'default'
      }));
  },

  // Filter Configuration
  filterableFields: ['name', 'isActive', 'isCompleted', 'description'],

  // Filter Groups for Basic/Advanced tabs
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic Filters',
      fields: ['name', 'isActive', 'isCompleted']
    },
    {
      name: 'advanced',
      label: 'Advanced Filters',
      fields: ['description', 'record_order']
    }
  ],

  // Custom Sort Orders
  customSortOrders: {
    isActive: [true, false],
    isCompleted: [true, false],
    record_order: 'asc'
  },

  // Form Layout Configuration
  layout: {
    mode: 'sections',
    sections: [
      {
        id: 'basic',
        title: 'CAPA Action Status Information',
        icon: '🏷️',
        columns: 2,
        fields: [
          { name: 'name' },
          { name: 'isActive' },
          { name: 'isCompleted' },
          { name: 'record_order' },
          { name: 'color' }
        ]
      },
      {
        id: 'description',
        title: 'Description',
        icon: '📄',
        columns: 1,
        fields: [
          { name: 'description' }
        ]
      }
    ]
  },

  // Tracking Section Configuration
  trackingSection: createTrackingConfig('CapaActionStatus'),

  // Labels Configuration
  labels: {
    singular: 'CAPA Action Status',
    plural: 'CAPA Action Statuses',
    addButton: 'Add CAPA Action Status',
    editButton: 'Edit CAPA Action Status',
    deleteButton: 'Delete CAPA Action Status',
    pageTitle: 'CAPA Action Status Management',
    pageSubtitle: 'Manage CAPA action statuses for tracking action progress',
    searchPlaceholder: 'Search statuses by name...',
    basicInfoSection: 'CAPA Action Status Information',
    activateCapaActionStatus: 'Activate CAPA Action Status',
    deactivateCapaActionStatus: 'Deactivate CAPA Action Status',
    reorderCapaActionStatuses: 'Reorder CAPA Action Statuses'
  },

  // Fields to remove during data preparation for submission
  fieldsToRemove: [
    'id', 'createdAt', 'updatedAt', 'createdById', 'updatedById',
    'actions', 'capaActionStatus'
  ],

  // Export filename for data exports
  exportFileName: 'capa-action-statuses',

  // Data transformation function for preparing data before API submission
  transformData: (data) => {
    const transformed = { ...data };
    
    // Convert record_order to integer or remove if empty
    if (transformed.record_order !== undefined) {
      if (transformed.record_order === '' || transformed.record_order === null) {
        delete transformed.record_order;
      } else {
        const numValue = parseInt(transformed.record_order, 10);
        if (!isNaN(numValue)) {
          transformed.record_order = numValue;
        } else {
          delete transformed.record_order;
        }
      }
    }
    
    return transformed;
  },

  // Messages Configuration
  messages: {
    loading: 'Loading CAPA action statuses...',
    noData: 'No CAPA action statuses found. Click "Add CAPA Action Status" to create the first status.',
    loadError: 'Failed to load CAPA action statuses',
    saveSuccess: 'CAPA action status saved successfully!',
    saveError: 'Failed to save CAPA action status',
    deleteSuccess: 'CAPA action status deleted successfully!',
    deleteError: 'Failed to delete CAPA action status',
    deleteConfirm: 'Are you sure you want to delete this CAPA action status? This action cannot be undone and may affect action records that reference this status.',
    activateSuccess: 'CAPA action status activated successfully!',
    deactivateSuccess: 'CAPA action status deactivated successfully!',
    createSuccess: 'CAPA action status created successfully!',
    updateSuccess: 'CAPA action status updated successfully!',
    validationError: 'Validation failed'
  },

  // Custom actions
  customActions: {
    activateCapaActionStatus: 'Activate CAPA Action Status',
    deactivateCapaActionStatus: 'Deactivate CAPA Action Status',
    reorderCapaActionStatuses: 'Reorder CAPA Action Statuses'
  },

  // Validation function for real-time field validation (dynamically assigned)
  validateField: null
};

export default capaActionStatusConfig;
